import { useMemo, useCallback } from 'react';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

export type FavoriteEntity = {
  id: string;
  name: string;
  entityKind: string; // The actual entity kind (workflow, journey, component, etc.)
};

/**
 * Create an entity reference string from a FavoriteEntity.
 * For workflows and journeys, use 'workflow' and 'journey' as kinds with slug as name.
 * For other catalog kinds, use standard catalog entity format.
 */
export function toolToEntityRef(item: FavoriteEntity): string {
  // If the id already looks like a full entityRef (kind:namespace/name), return as-is
  if (/^[^:]+:[^/]+\/.+$/.test(item.id)) {
    return item.id;
  }

  // Extract the kind and identifier from the id
  // Expected format: "workflow:{slug}" or "journey:{slug}" or other formats
  const parts = item.id.split(':');

  if (parts.length === 2) {
    const [type, identifier] = parts;

    // Map workflow/journey to their respective kinds
    if (type === 'workflow') {
      return stringifyEntityRef({
        kind: 'workflow',
        namespace: 'default',
        name: identifier,
      });
    }

    if (type === 'journey') {
      return stringifyEntityRef({
        kind: 'journey',
        namespace: 'default',
        name: identifier,
      });
    }
  }

  // For other catalog entities, use the id as-is (assuming it's already an entityRef)
  // or construct from the item properties
  return item.id;
}

/**
 * Extract FavoriteEntity data from an entity reference.
 */
export function entityRefToTool(entityRef: string): FavoriteEntity | null {
  try {
    // Try to parse as Backstage entity ref format (kind:namespace/name)
    const match = entityRef.match(/^([^:]+):([^/]+)\/(.+)$/);

    if (!match) {
      // Fallback: try legacy format (type:identifier)
      const parts = entityRef.split(':');
      if (parts.length === 2) {
        const [entityKind, identifier] = parts;
        return {
          id: entityRef,
          name: identifier,
          entityKind: entityKind.toLowerCase(),
        };
      }
      return null;
    }

    const [, kind, , name] = match;

    return {
      id: entityRef,
      name: name,
      entityKind: kind.toLowerCase(),
    };
  } catch {
    return null;
  }
}

/**
 * Hook that provides favorites functionality using Backstage's starred entities.
 * Returns FavoriteEntity items for all types (catalog entities, journeys, workflows).
 */
export function useFavorites(): FavoriteEntity[] {
  const { starredEntities } = useStarredEntities();

  return useMemo(() => {
    const tools: FavoriteEntity[] = [];

    for (const entityRef of starredEntities) {
      const tool = entityRefToTool(entityRef);
      if (tool) {
        tools.push(tool);
      }
    }

    return tools;
  }, [starredEntities]);
}

/**
 * Hook to check if a specific item is favorited.
 */
export function useIsFavorite(item: FavoriteEntity): boolean {
  const { isStarredEntity } = useStarredEntities();
  const entityRef = toolToEntityRef(item);
  return isStarredEntity(entityRef);
}

/**
 * Hook to toggle favorite status of an item.
 */
export function useToggleFavorite() {
  const { toggleStarredEntity } = useStarredEntities();

  return useCallback(
    (item: FavoriteEntity) => {
      const entityRef = toolToEntityRef(item);
      toggleStarredEntity(entityRef);
    },
    [toggleStarredEntity],
  );
}
